<template>
  <div
    v-if="visible"
    class="app-loading-screen ecran-public"
    role="status"
    aria-live="polite"
  >
    <div class="als-contenu">
      <img
        v-if="logoVisible"
        src="/logo-alertis.png"
        alt="Alertis"
        class="als-logo public-rise-1"
        @error="logoVisible = false"
      />

      <div class="als-attente public-rise-2">
        <PublicProgressTrack class="als-piste" />

        <div class="als-textes">
          <p class="als-phrase">Ouverture de votre espace</p>
          <p v-if="lent" class="als-lenteur public-rise-1">
            C'est plus long que d'habitude. Le serveur met du temps à répondre.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * ÉCRAN DE CHARGEMENT — ce que voit l'équipe pendant que l'outil vérifie sa session.
 *
 * Monté par App.vue pendant `checkAuth()`, sur les routes du back-office seulement
 * (un embed n'en a jamais). Il porte `.ecran-public`, comme les écrans publics.
 *
 * IL NE FLASHE PAS. L'écran attend 250 ms avant de se montrer : un démarrage
 * rapide — ou la fraction de seconde où le routeur résout `/login` — ne fait plus
 * clignoter la page.
 *
 * IL PARLE QUAND ÇA TRAÎNE. Passé 6 s d'attente, une phrase dit que le serveur
 * met du temps : un anneau muet se prend pour une panne.
 *
 * Le mouvement est lent, unique et horizontal (règle P23 de docs/PUBLIQUES-V2.md) ;
 * les blocs montent une fois (`public-rise-*` de src/styles/public.css), et tout
 * s'arrête sous `prefers-reduced-motion`.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import PublicProgressTrack from '../ui/public/PublicProgressTrack.vue'

const props = defineProps({
  isLoading: Boolean,
  isEmbedRoute: Boolean
})

const DELAI_APPARITION_MS = 250
const DELAI_LENTEUR_MS = 6000

const visible = ref(false)
const lent = ref(false)
const logoVisible = ref(true)

let minuteurApparition = null
let minuteurLenteur = null

const enAttente = computed(() => props.isLoading && !props.isEmbedRoute)

function arreterMinuteurs() {
  clearTimeout(minuteurApparition)
  clearTimeout(minuteurLenteur)
  minuteurApparition = null
  minuteurLenteur = null
}

watch(enAttente, (attend) => {
  arreterMinuteurs()
  visible.value = false
  lent.value = false
  if (!attend) return

  minuteurApparition = setTimeout(() => { visible.value = true }, DELAI_APPARITION_MS)
  minuteurLenteur = setTimeout(() => { lent.value = true }, DELAI_LENTEUR_MS)
}, { immediate: true })

onBeforeUnmount(arreterMinuteurs)
</script>

<style scoped>
.app-loading-screen {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--spacing-6);
  background: var(--color-bg-primary);
}

.als-contenu {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-8);
}

.als-logo {
  display: block;
  width: auto;
  height: 72px;
  max-width: 100%;
}

.als-attente {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-5);
}

/* Barre de progression indéterminée : `PublicProgressTrack` (220 px, 180 px
   sous 768 px ci-dessous ; mouvement réduit géré par le kit). */

.als-textes {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-3);
  max-width: 360px;
  text-align: center;
}

.als-phrase,
.als-lenteur {
  margin: 0;
  font-weight: 400;
  line-height: 1.5;
  color: var(--color-text-tertiary);
}

.als-phrase {
  font-size: var(--font-size-base);
}

.als-lenteur {
  font-size: var(--font-size-sm);
  text-wrap: pretty;
}

@media (max-width: 768px) {
  .als-contenu {
    gap: calc(var(--spacing-6) + var(--spacing-1));
  }

  .als-logo {
    height: 48px;
  }

  .als-piste {
    width: 180px;
  }
}
</style>
